import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';
import './TesteIA.css';

/**
 * Renderizador markdown leve sem depender de biblioteca externa.
 * Cobre: # h1-h6, **negrito**, *italico*, `code`, ```bloco```,
 * tabelas pipe |, listas - e 1., links [t](url), linhas horizontais ---.
 * Pra mais features avançadas, dá pra adicionar marked depois.
 */
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Bloco de código ```
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      blocks.push({ type: 'code', lang, content: buf.join('\n') });
      continue;
    }

    // Tabela: linha começa com | e tem |
    if (line.startsWith('|') && line.includes('|') && i + 1 < lines.length && /^[|\s\-:]+$/.test(lines[i+1])) {
      const header = line.split('|').slice(1, -1).map(s => s.trim());
      i += 2; // pula header + separador
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        rows.push(lines[i].split('|').slice(1, -1).map(s => s.trim()));
        i++;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { blocks.push({ type: 'h', level: h[1].length, content: h[2] }); i++; continue; }

    // Linha horizontal
    if (/^---+$/.test(line)) { blocks.push({ type: 'hr' }); i++; continue; }

    // Lista
    const listItem = line.match(/^[\s]*[-*]\s+(.+)$/) || line.match(/^[\s]*\d+\.\s+(.+)$/);
    if (listItem) {
      const items = [];
      while (i < lines.length) {
        const li = lines[i].match(/^[\s]*[-*]\s+(.+)$/) || lines[i].match(/^[\s]*\d+\.\s+(.+)$/);
        if (!li) break;
        items.push(li[1]);
        i++;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    // Parágrafo (uma ou mais linhas até linha vazia)
    if (line.trim() === '') { i++; continue; }
    const para = [];
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('#') && !lines[i].startsWith('|') && !/^[\s]*[-*]\s/.test(lines[i]) && !/^[\s]*\d+\.\s/.test(lines[i])) {
      para.push(lines[i]); i++;
    }
    blocks.push({ type: 'p', content: para.join(' ') });
  }

  // Render inline: **bold**, *italic*, `code`, [text](url)
  const renderInline = (txt) => {
    if (!txt) return null;
    const parts = [];
    let s = txt;
    const re = /(\*\*[^*]+?\*\*|\*[^*]+?\*|`[^`]+?`|\[[^\]]+?\]\([^)]+?\))/g;
    let last = 0;
    let m;
    while ((m = re.exec(s)) !== null) {
      if (m.index > last) parts.push(s.slice(last, m.index));
      const tk = m[0];
      if (tk.startsWith('**')) parts.push(<strong key={parts.length}>{tk.slice(2, -2)}</strong>);
      else if (tk.startsWith('`')) parts.push(<code key={parts.length} style={{ background:'#f0f0f0', padding:'1px 4px', borderRadius:3, fontFamily:'monospace', fontSize: '0.95em' }}>{tk.slice(1, -1)}</code>);
      else if (tk.startsWith('[')) {
        const linkM = tk.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (linkM) parts.push(<a key={parts.length} href={linkM[2]} target="_blank" rel="noopener noreferrer">{linkM[1]}</a>);
      }
      else parts.push(<em key={parts.length}>{tk.slice(1, -1)}</em>);
      last = m.index + tk.length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  };

  return blocks.map((b, idx) => {
    switch (b.type) {
      case 'h': {
        const Tag = `h${b.level}`;
        return <Tag key={idx} style={{ marginTop: b.level === 1 ? 0 : 24, marginBottom: 12 }}>{renderInline(b.content)}</Tag>;
      }
      case 'hr': return <hr key={idx} style={{ margin: '20px 0', border: 0, borderTop: '1px solid #e0e0e0' }} />;
      case 'p': return <p key={idx} style={{ lineHeight: 1.6 }}>{renderInline(b.content)}</p>;
      case 'list': return (
        <ul key={idx} style={{ lineHeight: 1.6 }}>
          {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
      case 'code': return (
        <pre key={idx} style={{ background:'#f5f5f5', padding:12, borderRadius:4, overflowX:'auto', fontSize:13 }}>
          <code>{b.content}</code>
        </pre>
      );
      case 'table': return (
        <div key={idx} style={{ overflowX: 'auto', marginBottom: 14 }}>
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:14 }}>
            <thead>
              <tr style={{ background:'#f5f5f5' }}>
                {b.header.map((h, j) => <th key={j} style={{ border:'1px solid #ddd', padding:'8px 12px', textAlign:'left' }}>{renderInline(h)}</th>)}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, j) => (
                <tr key={j}>
                  {row.map((cell, k) => <td key={k} style={{ border:'1px solid #ddd', padding:'8px 12px' }}>{renderInline(cell)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      default: return null;
    }
  });
}

export default function TesteIAAnaliseDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState('');

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ai-testes/analises/${id}`);
      setData(data);
      setDraft(data.conteudo_markdown);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    try {
      await axios.patch(`${API_URL}/api/ai-testes/analises/${id}`, { conteudo_markdown: draft });
      setEditMode(false);
      carregar();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return <div className="data-page"><p>Carregando...</p></div>;
  if (!data) return <div className="data-page"><p>Análise não encontrada.</p></div>;

  return (
    <div className="data-page" style={{ maxWidth: 980 }}>
      <div className="page-header" style={{ flexWrap: 'wrap' }}>
        <div>
          <button className="btn-link" onClick={() => navigate('/teste-ia/analises')}>← Voltar</button>
          <h1 style={{ margin: '8px 0 4px 0' }}>{data.titulo}</h1>
          <small style={{ color: '#666' }}>
            Criado por {data.criado_por_nome || '?'} em {new Date(data.created_at).toLocaleString('pt-BR')}
            {data.updated_at && data.updated_at !== data.created_at && <> · atualizado em {new Date(data.updated_at).toLocaleString('pt-BR')}</>}
          </small>
          {(data.runs_ids || []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              {data.runs_ids.map(r => (
                <Link key={r} to={`/teste-ia/${r}`} style={{ marginRight: 8, padding:'2px 10px', background:'#e3f2fd', borderRadius:12, textDecoration:'none', fontSize:13 }}>
                  run #{r}
                </Link>
              ))}
            </div>
          )}
        </div>
        <div>
          {!editMode ? (
            <button className="btn-secondary" onClick={() => setEditMode(true)}>✏️ Editar</button>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => { setEditMode(false); setDraft(data.conteudo_markdown); }} style={{ marginRight: 8 }}>Cancelar</button>
              <button className="btn-primary" onClick={salvar}>Salvar</button>
            </>
          )}
        </div>
      </div>

      {data.resumo && (
        <div style={{ background:'#fff8e1', padding:12, borderLeft:'4px solid #ffa726', marginBottom:20, fontStyle:'italic' }}>
          {data.resumo}
        </div>
      )}

      {editMode ? (
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          style={{ width:'100%', minHeight:600, fontFamily:'monospace', fontSize:13, padding:12, border:'1px solid #ccc', borderRadius:4 }}
        />
      ) : (
        <div style={{ background:'#fff', padding:24, borderRadius:6, border:'1px solid #eee' }}>
          {renderMarkdown(data.conteudo_markdown)}
        </div>
      )}
    </div>
  );
}
